// src/components/Globe/SensorModal.jsx - Modal Unificado para Hexágonos e Marcadores
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiX, 
  FiThermometer, 
  FiDroplet, 
  FiWind, 
  FiActivity,
  FiMapPin,
  FiTrendingUp,
  FiAlertCircle,
  FiCheckCircle,
  FiBarChart2,
  FiList,
  FiChevronRight
} from 'react-icons/fi';
import './SensorModal.css';

const SensorModal = ({ sensor, hexData, activeLayer, onClose, getMarkerColor }) => {
  const navigate = useNavigate();
  const [activeViewTab, setActiveViewTab] = useState('summary'); // 'summary' ou 'locations'
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(null);
  
  if (!sensor && !hexData) return null;
  
  // Se for hexágono com múltiplos sensores, usar o primeiro
  const displaySensor = sensor || (hexData?.sensors?.[0]);
  const isHexagon = !!hexData;
  const sensorCount = hexData?.sensors?.length || 1;
  
  // Calcular médias se for hexágono
  const averages = hexData?.averages || {};
  
  // Agrupar sensores por local (business_name ou location_name)
  const groupedByLocation = hexData ? hexData.sensors.reduce((acc, s) => {
    const locationKey = s.business_name || s.location_name || 
                       (s.city ? `${s.city}${s.state ? ` - ${s.state}` : ''}` : null) ||
                       `Local ${s.device_id}`;
    
    if (!acc[locationKey]) {
      acc[locationKey] = {
        locationName: locationKey,
        city: s.city,
        state: s.state || s.state_code,
        address: s.address,
        sensors: []
      };
    }
    
    acc[locationKey].sensors.push(s);
    return acc;
  }, {}) : {};
  
  const locations = Object.values(groupedByLocation);
  const selectedLocation = selectedLocationIndex !== null ? locations[selectedLocationIndex] : null;
  
  const handleViewDetails = (e) => {
    // Prevenir que o evento seja passado como parâmetro
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    
    // Sempre usar displaySensor (o sensor atual do modal)
    const targetSensor = displaySensor;
    
    // Usar device_id como prioridade (formato: "sensor:004846"), depois id
    const sensorId = targetSensor?.device_id || targetSensor?.id;
    
    if (!sensorId) {
      console.error('❌ Sensor sem device_id ou id:', targetSensor);
      alert('Erro: Sensor não possui ID válido. Não é possível abrir o dashboard.');
      return;
    }
    
    console.log(`✅ Navegando para sensor: ${sensorId}`);
    navigate(`/sensor/${sensorId}`);
    onClose();
  };
  
  const handleSensorClick = (sensor) => {
    handleViewDetails(sensor);
  };
  
  return (
    <AnimatePresence>
      <motion.div 
        className="sensor-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div 
          className="sensor-modal"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sensor-modal-header">
            <div className="sensor-modal-title">
              <h2>
                {isHexagon && sensorCount > 1 
                  ? `${sensorCount} Sensores nesta Área`
                  : displaySensor?.business_name || displaySensor?.location_name || 
                    (displaySensor?.is_anonymous 
                      ? `Dispositivo ${displaySensor?.device_id}`
                      : `${displaySensor?.city || 'Sensor'}, ${displaySensor?.country || ''}`)
                }
              </h2>
            </div>
            <button className="sensor-modal-close" onClick={onClose}>
              <FiX size={20} />
            </button>
          </div>
          
          {/* Tabs de Visualização - apenas se houver múltiplos sensores */}
          {isHexagon && sensorCount > 1 && (
            <div className="view-tabs">
              <button
                className={`view-tab ${activeViewTab === 'summary' ? 'active' : ''}`}
                onClick={() => {
                  setActiveViewTab('summary');
                  setSelectedLocationIndex(null);
                }}
              >
                <FiBarChart2 size={16} />
                <span>Resumo</span>
              </button>
              <button
                className={`view-tab ${activeViewTab === 'locations' ? 'active' : ''}`}
                onClick={() => {
                  setActiveViewTab('locations');
                  setSelectedLocationIndex(null);
                }}
              >
                <FiList size={16} />
                <span>Locais ({locations.length})</span>
              </button>
            </div>
          )}
          
          {/* Conteúdo */}
          <div className="sensor-modal-content">
            <AnimatePresence mode="wait">
              {isHexagon && sensorCount > 1 ? (
                activeViewTab === 'summary' ? (
                  <motion.div
                    key="summary"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="summary-content"
                  >
                    <h3 className="content-title">
                      <FiBarChart2 style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Médias da Área
                    </h3>
                    <div className="compact-stats-grid">
                      {activeLayer === 'airQuality' ? (
                        <>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">AQI Médio</span>
                            <span className="compact-stat-value" style={{ color: hexData.color }}>
                              {averages.aqi != null ? averages.aqi.toFixed(2) : '--'}
                            </span>
                          </div>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">Temp. Externa</span>
                            <span className="compact-stat-value">
                              {averages.externalTemperature != null 
                                ? averages.externalTemperature.toFixed(2) 
                                : averages.temperature != null 
                                ? averages.temperature.toFixed(2) 
                                : '--'}°C
                            </span>
                          </div>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">PM2.5</span>
                            <span className="compact-stat-value">
                              {averages.pm25 != null ? averages.pm25.toFixed(2) : '--'} µg/m³
                            </span>
                          </div>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">PM10</span>
                            <span className="compact-stat-value">
                              {averages.pm10 != null ? averages.pm10.toFixed(2) : '--'} µg/m³
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">Qualidade Média</span>
                            <span className="compact-stat-value" style={{ color: hexData?.color || getMarkerColor?.(displaySensor) }}>
                              {averages.quality != null ? averages.quality.toFixed(2) : '--'}%
                            </span>
                          </div>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">Temperatura</span>
                            <span className="compact-stat-value">
                              {averages.temperature != null ? averages.temperature.toFixed(2) : '--'}°C
                            </span>
                          </div>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">Umidade</span>
                            <span className="compact-stat-value">
                              {averages.humidity != null ? averages.humidity.toFixed(2) : '--'}%
                            </span>
                          </div>
                          <div className="compact-stat-item">
                            <span className="compact-stat-label">CO₂</span>
                            <span className="compact-stat-value">
                              {averages.co2 != null ? averages.co2.toFixed(2) : '--'} ppm
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="locations"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="locations-content"
                  >
                    {selectedLocationIndex === null ? (
                      <>
                        <h3 className="content-title">
                          <FiList style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Locais ({locations.length})
                        </h3>
                        <div className="locations-list">
                          {locations.map((location, index) => (
                            <div
                              key={index}
                              className="location-card"
                              onClick={() => setSelectedLocationIndex(index)}
                            >
                              <div className="location-header">
                                <div className="location-info">
                                  <FiMapPin size={16} style={{ marginRight: '8px', color: '#666' }} />
                                  <div>
                                    <h4 className="location-name">{location.locationName}</h4>
                                    {location.city && (
                                      <p className="location-city">
                                        {location.city}{location.state ? ` - ${location.state}` : ''}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                <div className="location-sensor-count">
                                  <span>{location.sensors.length}</span>
                                  <span className="sensor-label">
                                    {location.sensors.length === 1 ? 'sensor' : 'sensores'}
                                  </span>
                                </div>
                              </div>
                              {location.address && (
                                <p className="location-address">{location.address}</p>
                              )}
                              <div className="location-preview-stats">
                                {activeLayer === 'airQuality' ? (
                                  <>
                                    <span className="preview-stat">
                                      AQI: {location.sensors[0]?.aqi != null 
                                        ? location.sensors[0].aqi.toFixed(0) 
                                        : '--'}
                                    </span>
                                    <span className="preview-stat">
                                      Temp: {location.sensors[0]?.externalTemperature != null 
                                        ? location.sensors[0].externalTemperature.toFixed(1) 
                                        : location.sensors[0]?.temperature != null 
                                        ? location.sensors[0].temperature.toFixed(1) 
                                        : '--'}°C
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span className="preview-stat">
                                      Qualidade: {location.sensors[0]?.quality != null 
                                        ? location.sensors[0].quality.toFixed(0) 
                                        : '--'}%
                                    </span>
                                    <span className="preview-stat">
                                      Temp: {location.sensors[0]?.temperature != null 
                                        ? location.sensors[0].temperature.toFixed(1) 
                                        : '--'}°C
                                    </span>
                                  </>
                                )}
                              </div>
                              <FiChevronRight className="location-arrow" size={20} />
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="location-back-header">
                          <button 
                            className="back-btn"
                            onClick={() => setSelectedLocationIndex(null)}
                          >
                            ← Voltar
                          </button>
                          <h3 className="content-title">{selectedLocation.locationName}</h3>
                        </div>
                        <div className="sensors-list">
                          {selectedLocation.sensors.map((sensor, idx) => (
                            <div 
                              key={sensor.id || idx} 
                              className="sensor-card-compact"
                              onClick={() => handleSensorClick(sensor)}
                            >
                              <div className="sensor-card-header-compact">
                                <div>
                                  <h4 className="sensor-name-compact">
                                    {sensor.device_name || `Sensor ${sensor.device_id}`}
                                  </h4>
                                  {sensor.location_name && sensor.location_name !== selectedLocation.locationName && (
                                    <p className="sensor-location-compact">{sensor.location_name}</p>
                                  )}
                                </div>
                                <span className={`status-badge-compact ${sensor.status || 'offline'}`}>
                                  {sensor.status === 'online' ? (
                                    <FiCheckCircle size={14} />
                                  ) : (
                                    <FiAlertCircle size={14} />
                                  )}
                                </span>
                              </div>
                              
                              <div className="sensor-data-compact">
                                {activeLayer === 'airQuality' ? (
                                  <div className="data-grid-compact">
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">AQI:</span>
                                      <span className="data-value-compact" style={{ color: sensor.color || '#666' }}>
                                        {sensor.aqi != null ? sensor.aqi.toFixed(2) : '--'}
                                      </span>
                                    </div>
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">Temp. Externa:</span>
                                      <span className="data-value-compact">
                                        {sensor.externalTemperature != null 
                                          ? sensor.externalTemperature.toFixed(2) 
                                          : sensor.temperature != null 
                                          ? sensor.temperature.toFixed(2) 
                                          : '--'}°C
                                      </span>
                                    </div>
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">PM2.5:</span>
                                      <span className="data-value-compact">
                                        {sensor.pm25 != null ? sensor.pm25.toFixed(2) : '--'} µg/m³
                                      </span>
                                    </div>
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">PM10:</span>
                                      <span className="data-value-compact">
                                        {sensor.pm10 != null ? sensor.pm10.toFixed(2) : '--'} µg/m³
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="data-grid-compact">
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">Qualidade:</span>
                                      <span className="data-value-compact" style={{ color: sensor.color || '#666' }}>
                                        {sensor.quality != null ? sensor.quality.toFixed(2) : '--'}%
                                      </span>
                                    </div>
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">Temp:</span>
                                      <span className="data-value-compact">
                                        {sensor.temperature != null ? sensor.temperature.toFixed(2) : '--'}°C
                                      </span>
                                    </div>
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">Umidade:</span>
                                      <span className="data-value-compact">
                                        {sensor.humidity != null ? sensor.humidity.toFixed(2) : '--'}%
                                      </span>
                                    </div>
                                    <div className="data-item-compact">
                                      <span className="data-label-compact">CO₂:</span>
                                      <span className="data-value-compact">
                                        {sensor.co2 != null ? sensor.co2.toFixed(2) : '--'} ppm
                                      </span>
                                    </div>
                                  </div>
                                )}
                              </div>
                              
                              <div className="sensor-action-compact">
                                <span>Ver Dashboard</span>
                                <FiChevronRight size={16} />
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </motion.div>
                )
              ) : (
                <motion.div
                  key="single-sensor"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {!displaySensor?.hasData ? (
                    <div className="sensor-modal-no-data">
                      <FiAlertCircle className="no-data-icon" />
                      <p>Sem dados disponíveis</p>
                      <small>Aguardando transmissão do sensor</small>
                    </div>
                  ) : (
                    <>
                      <div className="sensor-modal-data">
                        {activeLayer === 'airQuality' ? (
                          <div className="sensor-modal-data-grid">
                            <div className="sensor-modal-data-item">
                              <span className="data-label">Air Quality Index:</span>
                              <span 
                                className="data-value" 
                                style={{ color: getMarkerColor?.(displaySensor) }}
                              >
                                {displaySensor?.aqi != null ? displaySensor.aqi.toFixed(2) : '--'}
                              </span>
                            </div>
                            <div className="sensor-modal-data-item">
                              <span className="data-label">Temperatura Externa:</span>
                              <span className="data-value">
                                {displaySensor?.externalTemperature != null 
                                  ? displaySensor.externalTemperature.toFixed(2) 
                                  : displaySensor?.temperature != null 
                                  ? displaySensor.temperature.toFixed(2) 
                                  : '--'}°C
                              </span>
                            </div>
                            <div className="sensor-modal-data-item">
                              <span className="data-label">PM2.5:</span>
                              <span className="data-value">
                                {displaySensor?.pm25 != null ? displaySensor.pm25.toFixed(2) : '--'} µg/m³
                              </span>
                            </div>
                            <div className="sensor-modal-data-item">
                              <span className="data-label">PM10:</span>
                              <span className="data-value">
                                {displaySensor?.pm10 != null ? displaySensor.pm10.toFixed(2) : '--'} µg/m³
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className="sensor-modal-data-grid">
                            <div className="sensor-modal-data-item">
                              <span className="data-label">Quality Score:</span>
                              <span 
                                className="data-value" 
                                style={{ color: getMarkerColor?.(displaySensor) }}
                              >
                                {displaySensor?.quality?.toFixed(1) || '--'}%
                              </span>
                            </div>
                            <div className="sensor-modal-data-item">
                              <span className="data-label">Temperature:</span>
                              <span className="data-value">
                                {displaySensor?.temperature?.toFixed(1) || '--'}°C
                              </span>
                            </div>
                            <div className="sensor-modal-data-item">
                              <span className="data-label">Humidity:</span>
                              <span className="data-value">
                                {displaySensor?.humidity?.toFixed(1) || '--'}%
                              </span>
                            </div>
                            <div className="sensor-modal-data-item">
                              <span className="data-label">CO₂:</span>
                              <span className="data-value">
                                {displaySensor?.co2 || '--'} ppm
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {displaySensor?.location_name && (
                        <div className="sensor-modal-location">
                          <FiMapPin className="location-icon" />
                          <span>{displaySensor.location_name}</span>
                        </div>
                      )}
                      
                      {displaySensor?.status && (
                        <div className="sensor-modal-status">
                          {displaySensor.status === 'online' ? (
                            <>
                              <FiCheckCircle className="status-icon online" />
                              <span>Online</span>
                            </>
                          ) : (
                            <>
                              <FiAlertCircle className="status-icon offline" />
                              <span>Offline</span>
                            </>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          
          {/* Footer com Botão - apenas para sensor individual */}
          {(!isHexagon || sensorCount === 1) && (
            <div className="sensor-modal-footer">
              <button 
                className="sensor-modal-details-btn"
                onClick={handleViewDetails}
              >
                Ver Detalhes Completos
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SensorModal;


